import React, { useContext, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { UploadSupportingDocumentsForConsultation } from '@/lib/apis/patients'
import useApiClient from '@/lib/apis/clients/useApiClient'
import { useStore } from 'zustand'
import { useStore as useGlobalStore } from '@/lib/store'
import {
  NotificationItem,
  NotificationItemVariant,
} from '@/lib/models/NotificationItem'
import { SupportingDocument } from '@/features/prescriptions/SupportingDocumentItem'
import { DocumentUploadFormContext } from '@/shared/request/DocumentUploadFormContext'
import posthog from 'posthog-js'
import QuizDocumentUpload, {
  type QuizDocumentUploadItem,
} from '@/features/quiz/screens/QuizDocumentUpload'
import { PrescriptionQuizFooter } from '@/features/prescriptions/request/PrescriptionQuizShell'
import { softMotionDuration } from '@/shared/components/motion/motion-tokens'

type Props = {}

const uploadCompletionSettleMs =
  Math.round(softMotionDuration.handoff * 1000) + 80

export default function DocumentUploadFormStage({}: Props) {
  const store = useContext(DocumentUploadFormContext)
  if (!store)
    throw new Error(
      'DocumentUploadFormStage must be used within a DocumentUploadFormContext',
    )

  const api = useApiClient()
  const uploadSupportingDocumentsMutation = useMutation(
    UploadSupportingDocumentsForConsultation.mutation(api),
  )

  const showNotification = useGlobalStore((state) => state.showNotification)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)

  const requestId = useStore(store, (s) => s.requestId)
  const supportingDocuments = useStore(store, (s) => s.supportingDocuments)
  const addSupportingDocuments = useStore(store, (s) => s.addSupportingDocument)
  const updateSupportingDocument = useStore(
    store,
    (s) => s.updateSupportingDocument,
  )
  const removeSupportingDocument = useStore(
    store,
    (s) => s.removeSupportingDocument,
  )

  const onPrev = useStore(store, (s) => s.onPrev)
  const onNext = useStore(store, (s) => s.onNext)

  const documentsAreUploading = supportingDocuments.some(
    (document) => document.uploadState.uploading,
  )

  function onAddDocument(document: SupportingDocument) {
    addSupportingDocuments(document)

    uploadSupportingDocumentsMutation.mutate(
      {
        document: {
          name: document.name,
          type: document.type,
          url: document.url,
        },

        creationToken: requestId,
        updateProgress: (progress) =>
          updateSupportingDocument(document.name, () => ({
            uploadState: { uploading: true, progress },
          })),
      },
      {
        onSuccess: async (token) => {
          // A small file can finish in one browser progress event. Keep the
          // uploading state around long enough for the determinate progress
          // animation to render its final frame before replacing the card.
          updateSupportingDocument(document.name, () => ({
            uploadState: { uploading: true, progress: 1 },
          }))
          await wait(uploadCompletionSettleMs)
          updateSupportingDocument(document.name, () => ({
            uploadState: { uploading: false, token },
          }))
        },
        onError: (err) => {
          posthog.captureException(err)
          console.error(err)
          removeSupportingDocument(document.name)

          showNotification(
            new NotificationItem(
              'Upload failed, please retry again. If this issue persists, please proceed without uploading the document.',
              NotificationItemVariant.Error,
            ),
          )
        },
      },
    )
  }

  function addFiles(files: File[]) {
    for (const file of files) {
      if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
        showNotification(
          new NotificationItem(
            'Please choose a PDF, JPEG or PNG file.',
            NotificationItemVariant.Error,
          ),
        )
        continue
      }

      if (file.size > 5 * 1024 * 1024) {
        showNotification(
          new NotificationItem(
            `${file.name} is larger than 5 MB.`,
            NotificationItemVariant.Error,
          ),
        )
        continue
      }

      if (supportingDocuments.some((document) => document.name === file.name)) {
        showNotification(
          new NotificationItem(
            `${file.name} has already been added.`,
            NotificationItemVariant.Error,
          ),
        )
        continue
      }

      onAddDocument({
        name: file.name,
        type: file.type,
        size: file.size,
        url: URL.createObjectURL(file),
        description: '',
        uploadState: { uploading: true, progress: 0 },
      })
    }
  }

  const quizDocuments: QuizDocumentUploadItem[] = supportingDocuments.map(
    (document) => ({
      id: document.name,
      name: document.name,
      sizeLabel: formatFileSize(document.size),
      fileTypeLabel: fileTypeLabel(document.type),
      kind: document.type.startsWith('image') ? 'image' : 'document',
      status: document.uploadState.uploading
        ? {
            type: 'uploading' as const,
            progress: document.uploadState.progress,
          }
        : { type: 'uploaded' as const },
      note: document.description,
      thumbnailUrl: document.type.startsWith('image')
        ? document.url
        : undefined,
    }),
  )

  return (
    <div className="w-full">
      <input
        accept=".pdf,image/jpeg,image/png"
        className="sr-only"
        multiple
        onChange={(event) => {
          addFiles(Array.from(event.target.files ?? []))
          event.target.value = ''
        }}
        ref={fileInputRef}
        type="file"
      />
      <QuizDocumentUpload
        busy={documentsAreUploading}
        documents={quizDocuments}
        dragActive={dragActive}
        onAddDocument={() => fileInputRef.current?.click()}
        onDragActiveChange={setDragActive}
        onDropDocuments={addFiles}
        onNoteChange={(name, description) =>
          updateSupportingDocument(name, () => ({ description }))
        }
        onRemoveDocument={(name) => removeSupportingDocument(name)}
      />

      <PrescriptionQuizFooter
        backDisabled={documentsAreUploading}
        nextDisabled={documentsAreUploading}
        nextLoading={documentsAreUploading}
        onBack={onPrev}
        onNext={onNext}
      />
    </div>
  )
}

function formatFileSize(size?: number) {
  if (!size) return 'File'
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

function fileTypeLabel(type: string) {
  if (type === 'application/pdf') return 'PDF'
  if (type === 'image/png') return 'PNG'
  if (type === 'image/jpeg') return 'JPEG'
  return 'Document'
}

function wait(durationMs: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, durationMs))
}
